import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import { useAuth } from '@sopranodesign/pulse-auth';
import Icon from '@sopranodesign/pulse-icons';

import { fetchJiraIssues } from '../api';
import { StateLevel } from '.';

const JiraIssuesList = ({
  projectId,
  handleHide,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission('edit generative ai resource');

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: `jira-${projectId}` });
  const pagination = usePaginationReducer({ id: `jira-${projectId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/jira/issues',
      projectId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchJiraIssues(projectId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const issues = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const projectName = response.project_name ?? 'Issues';

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  return (
    <AnimatePresence>
      <PanelSlider header={projectName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !issues.length && (
          <Paragraph mb secondary>No issues available.</Paragraph>
        )}
        {!isLoading && issues.length > 0 && (
          <Flex col>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Issue', sort: 'summary' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canEditResource ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {issues.map(issue => (
                    <TableRow key={issue.id}>
                      <TableCell>
                        <Flex center>
                          <span>{issue.issue_key} — {issue.summary}</span>
                          {issue.attachment_count > 0 && (
                            <Flex ml center title={`${issue.attachment_count} attachment(s)`}>
                              <Icon type="document" size="1rem" width="1rem" height="1rem" />
                              <span>{issue.attachment_count}</span>
                            </Flex>
                          )}
                        </Flex>
                      </TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={issue.status} />
                          <span>{issue.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{issue.updated_at}</TableCell>

                      {canEditResource && (
                        <TableActions>
                          <Button
                            icon="download"
                            loading={Boolean(loadingIds?.[issue.id])}
                            onClick={() => onOpenRecrawlModal(issue)}
                          >
                            Recrawl
                          </Button>

                          {errorIds?.[issue.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[issue.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

JiraIssuesList.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object
};

export default JiraIssuesList;
